import React from "react";
import _ from "lodash";
import anime from "animejs";
import Responsive from "react-responsive";

import Button from "../../components/Button";
import ButtonGroup from "../../components/ButtonGroup";
import { Modal } from "../../components";
import GalleryLightbox from "../../components/GalleryLightbox";

import Compass from "./components/Compass";
import Bus from "./components/Bus";

import Pin from "../../components/svg/Pin";
import MultiLineText from "../../components/svg/MultiLineText";

import { assetsPath } from "../../utils";
import { isIE } from "../../utils/detector";

import { texts, RUtexts, CHtexts } from "./common";
import commonPins from "./common-pins";
import logos from "./common-logos";

const Mobile = (props) => <Responsive {...props} maxWidth={767} />;
const Desktop = (props) => <Responsive {...props} minWidth={768} />;

const fixIETransform = (id) => (anim) => {
  const g = document.getElementById(id);
  if (g) {
    const transform = getComputedStyle(g).getPropertyValue("transform");
    g.setAttribute("transform", transform);
  }
};

const contentPath = process.env.REACT_APP_CLOUDFRONT;

const iconHotel = `${contentPath}/assets/images/project-map/project-icon-01.png`;
const iconRestaurant = `${contentPath}/assets/images/project-map/project-icon-02.png`;
const iconSpa = `${contentPath}/assets/images/project-map/project-icon-03.png`;
const iconGallery = `${contentPath}/assets/images/project-map/project-icon-04.png`;
const iconBus = `${contentPath}/assets/images/project-map/project-icon-05.png`;
const iconBoat = `${contentPath}/assets/images/project-map/project-icon-06.png`;

const INITIAL_STATE = {
  currentPage: "hotel",
  lightboxIsOpen: false,
  lightboxCurrentImage: 0,
  lightboxImages: [],
};

const overwriteMerge = (destinationArray, sourceArray, options) => {
  const destination = destinationArray.slice();

  sourceArray.forEach((e, i) => {
    Object.keys(e).forEach((key) => {
      // console.log(destination[0][key])
      destination[i][key] = sourceArray[i][key];
    });
  });

  return destination;
};

const switchLanguage = (activeLanguage) => {
  switch (activeLanguage) {
    case "cn":
      return CHtexts;
    case "ru":
      return RUtexts;
    default:
      return texts;
  }
};

// Render Side Button
const renderButton = ({ value, label, icon, active, type, key, onClick }) => {
  return (
    <Button
      key={key}
      type="default"
      active={active}
      activeStyle={{ backgroundColor: "#ffffff", color: "black" }}
      hoverStyle={{ backgroundColor: "#ffffff", color: "black" }}
      defaultStyle={{
        marginRight: "3px",
        backgroundColor: "#dbdbdb",
        padding: "2px 5px 2px 5px",
        letterSpacing: 0,
        color: "black",
        marginBottom: "3px",
      }}
      onClick={onClick}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <img
          src={icon}
          width={23}
          height={23}
          style={{ marginRight: 7 }}
          alt=""
        />
        {label}
      </div>
    </Button>
  );
};

class ProjectMap extends React.Component {
  static defaultProps = {
    texts,
    pins: commonPins,
  };

  state = INITIAL_STATE;

  playBusRoute() {
    // Bus 1
    const bus1Paths = [
      { path: anime.path("#bus-trail1"), duration: 5000 },
      { path: anime.path("#bus-trail2"), duration: 4000 },
      { path: anime.path("#bus-trail3"), duration: 4000 },
      { path: anime.path("#bus-trail4"), duration: 5000 },
      { path: anime.path("#bus-trail5"), duration: 5000 },
    ];

    const bus1Timeline = _.reduce(
      bus1Paths,
      (timeline, busPath) => {
        return timeline.add({
          targets: "#bus-1",
          translateX: busPath.path("x"),
          translateY: busPath.path("y"),
          easing: "easeInOutQuad",
          duration: busPath.duration,
          delay: 500,
          update: isIE ? fixIETransform("bus-1") : null,
        });
      },
      anime.timeline({
        direction: "alternate",
        loop: true,
        delay: 500,
      })
    );

    bus1Timeline.play();

    // Bus 2
    const bus2Paths = [
      // { path: anime.path("#bus-trail1"), duration: 5000 },
      { path: anime.path("#bus-trail2"), duration: 4000 },
      { path: anime.path("#bus-trail3"), duration: 4000 },
      { path: anime.path("#bus-trail4"), duration: 5000 },
      // { path: anime.path("#bus-trail5"), duration: 5000 },
    ];

    const bus2Timeline = _.reduce(
      bus2Paths,
      (timeline, busPath) => {
        return timeline.add({
          targets: "#bus-2",
          translateX: busPath.path("x"),
          translateY: busPath.path("y"),
          easing: "easeInOutQuad",
          duration: busPath.duration,
          delay: 500,
          update: isIE ? fixIETransform("bus-2") : null,
        });
      },
      anime.timeline({
        direction: "alternate",
        loop: true,
        delay: 3500,
      })
    );

    bus2Timeline.play();

    // Bus 2
    const bus3Paths = [
      { path: anime.path("#bus-trail1"), duration: 5000 },
      { path: anime.path("#bus-trail2"), duration: 4000 },
      { path: anime.path("#bus-trail3"), duration: 4000 },
      { path: anime.path("#bus-trail4"), duration: 5000 },
      { path: anime.path("#bus-trail5"), duration: 5000 },
    ];

    const bus3Timeline = _.reduce(
      bus3Paths,
      (timeline, busPath) => {
        return timeline.add({
          targets: "#bus-3",
          translateX: busPath.path("x"),
          translateY: busPath.path("y"),
          easing: "easeInOutQuad",
          duration: busPath.duration,
          delay: 500,
          update: isIE ? fixIETransform("bus-3") : null,
        });
      },
      anime.timeline({
        direction: "alternate",
        loop: true,
        delay: 500,
      })
    );

    bus3Timeline.seek(bus3Timeline.duration);
    bus3Timeline.reverse();
  }

  playBoatRoute() {
    const boatPaths = [
      { path: anime.path("#boat-trail1"), duration: 3000 },
      { path: anime.path("#boat-trail2"), duration: 2000 },
      { path: anime.path("#boat-trail3"), duration: 2000 },
      { path: anime.path("#boat-trail4"), duration: 3000 },
      { path: anime.path("#boat-trail5"), duration: 3000 },
      { path: anime.path("#boat-trail6"), duration: 3000 },
      { path: anime.path("#boat-trail7"), duration: 2000 },
      { path: anime.path("#boat-trail8"), duration: 2000 },
      { path: anime.path("#boat-trail9"), duration: 3000 },
      { path: anime.path("#boat-trail10"), duration: 3000 },
      { path: anime.path("#boat-trail11"), duration: 3000 },
    ];

    const boatTimeline = _.reduce(
      boatPaths,
      (timeline, boatPath) => {
        return timeline.add({
          targets: "#boat",
          translateX: boatPath.path("x"),
          translateY: boatPath.path("y"),
          easing: "easeInOutQuad",
          duration: boatPath.duration || 5000,
          delay: 500,
          update: isIE ? fixIETransform("boat") : null,
        });
      },
      anime.timeline({
        direction: "alternate",
        loop: true,
        delay: 500,
      })
    );

    boatTimeline.play();
  }

  componentDidUpdate() {
    if (this.state.currentPage === "boat") this.playBoatRoute();
    if (this.state.currentPage === "bus") this.playBusRoute();
  }

  reset = () => {
    this.setState(INITIAL_STATE);
  };

  handleChangePage = (page) => this.setState({ currentPage: page });

  filterTextsByPage = (page) => {
    const { currentLanguage, project } = this.props;
    const selActiveLanguage = switchLanguage(currentLanguage);
    const mergedTexts = _.merge(_.cloneDeep(texts), selActiveLanguage, {
      arrayMerge: overwriteMerge,
    });
    return _.filter(mergedTexts, (text, i) => {
      const excludeList = _.get(text, "options.excludes", []);
      if (
        _.includes(excludeList, `project-map/${page}`) ||
        _.includes(excludeList, "project-map") ||
        _.includes(excludeList, `project-map/project/${project}`)
      )
        return false;
      return true;
    });
  };

  filterTextsForMobile = (page) => {
    const { currentLanguage, project } = this.props;
    const selActiveLanguage = switchLanguage(currentLanguage);
    const mergedTexts = _.merge(_.cloneDeep(texts), selActiveLanguage, {
      arrayMerge: overwriteMerge,
    });
    return _.filter(mergedTexts, (text, i) => {
      const excludeList = _.get(text, "options.excludes", []);
      if (
        _.includes(excludeList, `masterplan`) ||
        _.includes(excludeList, `project-map/project/${project}`)
      )
        return false;
      return true;
    });
  };
  handleClickPin = (markerProps) => {
    this.setState({
      lightboxIsOpen: true,
      lightboxCurrentImage: 0,
      lightboxImages: markerProps.images,
    });
  };
  gotoPrevious = () => {
    this.setState({
      currentImage: this.state.currentImage - 1,
    });
  };
  gotoNext = () => {
    this.setState({
      currentImage: this.state.currentImage + 1,
    });
  };
  gotoImage = (index) => {
    this.setState({
      currentImage: index,
    });
  };

  closeLightbox = () => {
    this.setState({ lightboxIsOpen: false });
  };

  gotoPrevious = () => {
    this.setState({
      lightboxCurrentImage: this.state.lightboxCurrentImage - 1,
    });
  };

  gotoNext = () => {
    this.setState({
      lightboxCurrentImage: this.state.lightboxCurrentImage + 1,
    });
  };

  gotoImage = (index) => {
    this.setState({
      lightboxCurrentImage: index,
    });
  };
  renderTextBubble = (bubbleProps, markerProps) => {
    const { text, width, style } = bubbleProps;
    // const { currentLanguage } = markerProps;
    const offsetX = 20;
    const rectWidth = 2 * offsetX + width;
    // const translateText = _.get(text, currentLanguage, ['']);
    return (
      <g>
        <rect
          x="0"
          y="0"
          width={rectWidth}
          height="40"
          rx="20"
          ry="20"
          fill={markerProps.color}
        />
        <MultiLineText
          text={text}
          x={offsetX}
          y={4}
          textAnchor="start"
          width={width}
          height={32}
          style={{ ...style, fill: "white" }}
        />
      </g>
    );
  };

  renderRestaurantBubble = (bubbleProps, markerProps) => {
    const { t } = this.props;
    const { url, width, height, num } = bubbleProps;
    const arrowSize = 10;
    const offsetX = 20;
    const rectWidth = 2 * offsetX + (32 * width) / height;
    return (
      <g>
        <rect
          x="0"
          y="0"
          width={rectWidth}
          height="68"
          rx="20"
          ry="20"
          fill="#d68000"
        />
        <image
          x={offsetX}
          y={4}
          width={(32 * width) / height}
          xlinkHref={url}
          height={32}
        />
        <rect
          x={0}
          y={40}
          width={rectWidth}
          height={1}
          fill="white"
          opacity={0.5}
        />
        <text
          x={0.5 * rectWidth}
          y={56}
          width={rectWidth}
          height={28}
          textAnchor="middle"
          style={{ fill: "white", fontSize: 10 }}
        >
          {num} {num === 1 ? t("Restaurant") : t("Restaurants")}
        </text>
        <svg
          viewBox="0 0 100 100"
          x={rectWidth - 28 + 0.5 * (28 - arrowSize)}
          y={40 + 0.5 * (28 - arrowSize) - 1}
          width={arrowSize}
          height={arrowSize}
        >
          <path
            d="M25.5,100 75.5,50 25.5,0"
            fill="none"
            stroke="white"
            strokeWidth={5}
          />
        </svg>
      </g>
    );
  };

  render() {
    const {
      pins,
      t,
      currentLanguage,
      project = "phuket-skypark",
      children,
    } = this.props;

    // const baseAssetUrl = assetsPath("phuket-laguna-park-2");
    // const baseAssetUrl = assetsPath("phuket-skypark");
    const baseAssetUrl = assetsPath(project);
    const bg = `${baseAssetUrl}/images/project-map/phuket-masterplan-${currentLanguage}.png`;
    // const bg = `${baseAssetUrl}/images/project-map/phuket-masterplan-no-logo-${currentLanguage}.png`;
    const {
      currentPage,
      lightboxIsOpen,
      lightboxCurrentImage,
      lightboxImages,
    } = this.state;
    const filteredTexts = this.filterTextsByPage(currentPage);
    const filteredTextsMobile = this.filterTextsForMobile(currentPage);
    const pinsToRender = _.map(pins, (pin) => {
      if (pin.group === "restaurant") {
        return {
          ...pin,
          bubble: {
            ...pin.bubble,
            render: this.renderRestaurantBubble,
          },
        };
      }

      if (pin.group === "spa" || pin.group === "gallery") {
        return {
          ...pin,
          bubble: {
            ...pin.bubble,
            render: this.renderTextBubble,
          },
        };
      }
      return pin;
    });

    console.log("render-project-map", project);

    return (
      <div className="project-map-container">
        {/* <div className="page-menu">
          <ButtonGroup
            value={this.state.currentPage}
            items={[
              {
                value: "hotel",
                label: t("Hotels & Resorts"),
                icon: iconHotel,
              },
              {
                value: "restaurant",
                label: t("Restaurants"),
                icon: iconRestaurant,
              },
              { value: "spa", label: t("Spas"), icon: iconSpa },
              {
                value: "gallery",
                label: t("Galleries & Shopping"),
                icon: iconGallery,
              },
              { value: "bus", label: t("Shuttle Bus"), icon: iconBus },
              { value: "boat", label: t("Shuttle Boat"), icon: iconBoat },
            ]}
            direction="vertical"
            onChange={this.handleChangePage}
            renderItem={renderButton}
          />
        </div> */}
        <svg className="project-map" viewBox="0 0 1730 1080" width="100%">
          <image xlinkHref={bg} x={0} y={0} width="100%" height={1080} />
          {/* <Mobile>
            <g>
              {logos.map((logo, index) => (
                <image
                  key={`logo-${index}`}
                  xlinkHref={logo.path}
                  x={logo.x}
                  y={logo.y}
                  width={logo.width}
                  height={logo.height}
                />
              ))}
            </g>
          </Mobile> */}
          {/* <Compass x={630} y={50} width={44} height={44} /> */}
          {currentPage === "bus" && (
            <g
              // style={{ transform: "scale(0.96) translate(73px, -25px)" }}
              style={{ transform: "scale(0.824) translate(230px, 20px)" }}
            >
              <path
                id="bus-trail1"
                class="bus-trail"
                d="M261,212.5c0,0,0.3,1.8-0.7,6c-1,4.2-22,64.5-21,79.2c1,14.7,9.3,17.5,12.7,20
                s9.2,8.7,13,12.2s8,3.8,13,10c5,6.2,1,22.7,4.2,27.9c3.2,5.2,26,14.5,34.7,19.2c8.7,4.7,16.8,16.8,16.8,16.8s7.2-2.3,12-4
                c4.8-1.7,8.2-1.7,12-3.7c3.8-2,8.3-6,12.2-14.3c3.8-8.3,6.8-17.5,13.3-21.7s16.5-1.8,24.7-3.3c3.3-0.6,10.2-4.3,17.3-10.7
                c18-16.1,39.8-42.8,44.1-52.3c4.5-10.1,2.1-18.5,1.9-23.4c-0.2-4.9,2.3-2.6,2.3-19.3"
              />
              <path
                id="bus-trail2"
                class="bus-trail"
                d="M473.5,250.9c0,16.7-2.5,14.4-2.3,19.3c0.2,4.9,2.6,13.3-1.9,23.4
                c-4.2,9.5-26.1,36.3-44.1,52.3c0,0,10.3,10.2,14.1,16.8s5.8,19.5,6,27.8s-0.2,19.2,1.2,22.5s5.8,8,5.8,8s-3.6,0-6.2,1.5
                c-3.3,1.9-9.4,9.6-12.6,16c-5.8,11.3-12,21.5-12,21.5"
              />
              <path
                id="bus-trail3"
                class="bus-trail"
                d="M421.4,460c0,0,6.2-10.2,12-21.5c3.2-6.4,9.3-14.1,12.6-16c2.6-1.5,6.2-1.5,6.2-1.5
                s8.5,2.5,14.8,2.2s30.8-3.5,37.4-6.6s22.1-14,34.9-23.1s20.3-12.4,32.7-13.5s31.2,0,31.2,0s1.5-8.9,1.5-23.6s-1.8-20.8-1.5-24.4
                s1.5-4,1.5-4"
              />
              <path
                id="bus-trail4"
                class="bus-trail"
                d="M604.8,328c0,0-1.2,0.4-1.5,4s1.5,9.6,1.5,24.4s-1.5,23.6-1.5,23.6s14.2,1.1,20.9,2.5
                s19.6,4.8,29,7.4s19.8,5.4,19.8,5.4s3.1,8.6,4.1,16.9s1.9,14.3,3,19.2c1.1,4.9,3.2,7.8,5.2,16.4s5,20.4,5,20.4"
              />
              <path
                id="bus-trail5"
                class="bus-trail"
                d="M690.2,468.1c0,0-3-11.8-5-20.4s-4.2-11.4-5.2-16.4c-1.1-5-2-11-3-19.2
                s-4.1-16.9-4.1-16.9s4.5,1.9,12.8-1.5s23-14.4,26.9-18s18.8-12.6,29-23.6s15.1-21,22.4-26.2s25.1-9.5,32.6-15.8s12.5-12,21-12.8
                s10,0,10,0"
              />
            </g>
          )}
          {/* {currentPage === "boat" && (
            <g>
              <path
                id="boat-trail11"
                className="boat-trail"
                d="M802.5,254c8-0.5,15-1.7,21.7-1.2c6.8,0.5,16.4,5.7,24.9,5.2s14.5-5.8,26.5-4.7
                S900,267,900,267"
              />
              <path
                id="boat-trail10"
                className="boat-trail"
                d="M646.5,325c0,0,43-6.5,59.5-17s37.3-43.3,56.5-49.5c12-3.9,32-4,40-4.5"
              />
              <path
                id="boat-trail9"
                className="boat-trail"
                d="M583,265.5c0,0-8.5,28,1.5,41.5c6.5,8,48.7,4.5,62,8c7.3,1.9,5,9.5,0,10"
              />
              <path
                id="boat-trail8"
                className="boat-trail"
                d="M627,255.5c0,0-29.5-28.5-42.5-19s-1.5,29-1.5,29"
              />
              <path
                id="boat-trail7"
                className="boat-trail"
                d="M621.5,219.5c0,0,4,9,5.5,16s0,20,0,20"
              />
              <path
                id="boat-trail6"
                className="boat-trail"
                d="M595.5,209c0,0,2.3,8.5,9.8,10.5s16.2,0,16.2,0"
              />
              <path
                id="boat-trail5"
                className="boat-trail"
                d="M542,276c0,0,11.7-27.7,26.2-46.5c7.3-9.5,24.7-19.5,27.3-20.5"
              />
              <path
                id="boat-trail4"
                className="boat-trail"
                d="M396.5,311.5c0,0,4.9-33.7,15.7-39.7s31.7-0.4,63.2-1c27.1-0.5,55.1-3.6,61-3.5
                c5.5,1,5.6,8.7,5.6,8.7"
              />
              <path
                id="boat-trail3"
                className="boat-trail"
                d="M426,229c0,0-17.9,10.6-26.8,38c-8.8,27.2-2.8,44.5-2.8,44.5"
              />
              <path
                id="boat-trail2"
                className="boat-trail"
                d="M408.5,211.5c0,0-2,6.5,3.5,11.5s14,6,14,6"
              />
              <path
                id="boat-trail1"
                className="boat-trail"
                d="M297.5,211.5c0,0,25,13.5,55.5,14s55.5-14,55.5-14"
              />
            </g>
          )} */}
          <g
            className="xxxx"
            style={{ transform: "scale(0.824) translate(230px, 20px)" }}
            // style={{ transform: "scale(0.96) translate(73px, -25px)" }}
          >
            {currentPage === "boat" && (
              <g>
                <path
                  id="boat-trail11"
                  className="boat-trail"
                  d="M802.5,254c8-0.5,15-1.7,21.7-1.2c6.8,0.5,16.4,5.7,24.9,5.2s14.5-5.8,26.5-4.7
                  S900,267,900,267"
                />
                <path
                  id="boat-trail10"
                  className="boat-trail"
                  d="M646.5,325c0,0,43-6.5,59.5-17s37.3-43.3,56.5-49.5c12-3.9,32-4,40-4.5"
                />
                <path
                  id="boat-trail9"
                  className="boat-trail"
                  d="M583,265.5c0,0-8.5,28,1.5,41.5c6.5,8,48.7,4.5,62,8c7.3,1.9,5,9.5,0,10"
                />
                <path
                  id="boat-trail8"
                  className="boat-trail"
                  d="M627,255.5c0,0-29.5-28.5-42.5-19s-1.5,29-1.5,29"
                />
                <path
                  id="boat-trail7"
                  className="boat-trail"
                  d="M621.5,219.5c0,0,4,9,5.5,16s0,20,0,20"
                />
                <path
                  id="boat-trail6"
                  className="boat-trail"
                  d="M595.5,209c0,0,2.3,8.5,9.8,10.5s16.2,0,16.2,0"
                />
                <path
                  id="boat-trail5"
                  className="boat-trail"
                  d="M542,276c0,0,11.7-27.7,26.2-46.5c7.3-9.5,24.7-19.5,27.3-20.5"
                />
                <path
                  id="boat-trail4"
                  className="boat-trail"
                  d="M396.5,311.5c0,0,4.9-33.7,15.7-39.7s31.7-0.4,63.2-1c27.1-0.5,55.1-3.6,61-3.5
                  c5.5,1,5.6,8.7,5.6,8.7"
                />
                <path
                  id="boat-trail3"
                  className="boat-trail"
                  d="M426,229c0,0-17.9,10.6-26.8,38c-8.8,27.2-2.8,44.5-2.8,44.5"
                />
                <path
                  id="boat-trail2"
                  className="boat-trail"
                  d="M408.5,211.5c0,0-2,6.5,3.5,11.5s14,6,14,6"
                />
                <path
                  id="boat-trail1"
                  className="boat-trail"
                  d="M297.5,211.5c0,0,25,13.5,55.5,14s55.5-14,55.5-14"
                />
              </g>
            )}
            {currentPage === "boat" && (
              <g id="boat">
                <svg
                  viewBox="0 0 81 109"
                  x={-20}
                  y={-52}
                  width={40}
                  height={52}
                >
                  <path
                    fill="#a28e81"
                    d="M81,40.5C81,72,40.83,109,40.83,109S0,71,0,40.5a40.5,40.5,0,0,1,81,0Z"
                  />
                  <g transform={`scale(0.6) translate(30 20)`}>
                    <path
                      fill="white"
                      d="M3.5,74.2c21.1-2.5,44.9-5.4,66-7.9c-2.9,8.1-9.5,11.4-12.9,16.9c-15-6.9-32.6,4.9-49.9,0
	C5.6,80.2,4.6,77.2,3.5,74.2L3.5,74.2z M35,0.6c0,1.9,0,3.8,0,5.6C30.6,4.7,26.1,4,21.7,4.2l0,1.8c-3.9,1.2-7.7,0.3-11.4-0.7
	c2.1-0.3,4.2-0.4,6.3-0.8l-0.2-2.2C22.5-0.3,28.7-0.4,35,0.6L35,0.6z M0,83.3c8.4,4.4,20.2,7.8,35.3,4.7c13.8-2.9,23,0.7,34.8-1.2
	c-12.1-0.1-16.9-5.3-25.3-3.9C13.5,89,9.6,85.1,0,83.3L0,83.3z M34.1,67.5V17.7L2.4,65.8L34.1,67.5L34.1,67.5z M63.8,65.4
	C67,40,56.1,21.6,35.5,7.9c3.8,23,4.2,43.2,1,60.8C45.8,67.5,55,66.4,63.8,65.4z"
                    />
                  </g>
                </svg>
              </g>
            )}
          </g>
          <g
            id="pins"
            className="d-none d-md-block"
            // style={{ transform: "scale(0.824) translate(230px, 20px)" }}
            style={{ transform: "scale(0.96) translate(75px, -25px)" }}
          >
            {pinsToRender.map((pin, i) => (
              <Pin
                t={t}
                key={`pin-${i}`}
                {...pin}
                // x={100}
                // y={100}
                currentLanguage={currentLanguage}
                onClick={(pinProps) => this.handleClickPin(pinProps)}
                visible={pin.group === currentPage}
              />
            ))}
          </g>
          {currentPage === "bus" && (
            <g
              // style={{ transform: "scale(0.96) translate(73px, -25px)" }}
              style={{ transform: "scale(0.824) translate(230px, 20px)" }}
            >
              <g id="bus-1">
                <Bus />
              </g>
              <g id="bus-2">
                <Bus />
              </g>
              <g id="bus-3">
                <Bus />
              </g>
            </g>
          )}
          {/* {currentPage === "boat" && (
            <g
              id="boat"
            >
              <svg viewBox="0 0 81 109" x={-20} y={-52} width={40} height={52}>
                <path
                  fill="#a28e81"
                  d="M81,40.5C81,72,40.83,109,40.83,109S0,71,0,40.5a40.5,40.5,0,0,1,81,0Z"
                />
                <g transform={`scale(0.6) translate(30 20)`}>
                  <path
                    fill="white"
                    d="M3.5,74.2c21.1-2.5,44.9-5.4,66-7.9c-2.9,8.1-9.5,11.4-12.9,16.9c-15-6.9-32.6,4.9-49.9,0
	C5.6,80.2,4.6,77.2,3.5,74.2L3.5,74.2z M35,0.6c0,1.9,0,3.8,0,5.6C30.6,4.7,26.1,4,21.7,4.2l0,1.8c-3.9,1.2-7.7,0.3-11.4-0.7
	c2.1-0.3,4.2-0.4,6.3-0.8l-0.2-2.2C22.5-0.3,28.7-0.4,35,0.6L35,0.6z M0,83.3c8.4,4.4,20.2,7.8,35.3,4.7c13.8-2.9,23,0.7,34.8-1.2
	c-12.1-0.1-16.9-5.3-25.3-3.9C13.5,89,9.6,85.1,0,83.3L0,83.3z M34.1,67.5V17.7L2.4,65.8L34.1,67.5L34.1,67.5z M63.8,65.4
	C67,40,56.1,21.6,35.5,7.9c3.8,23,4.2,43.2,1,60.8C45.8,67.5,55,66.4,63.8,65.4z"
                  />
                </g>
              </svg>
            </g>
          )} */}
          {children}
        </svg>
        {currentPage === "boat" && (
          <div className="boat-legend">
            <ul>
              <li>
                <span>1</span>
                {t("Outrigger Hotel Phuket Beach Resort")}
              </li>
              <li>
                <span>2</span>
                {t("Cassia Beach")}
              </li>
              <li>
                <span>3</span>
                {t("Dusit Thani Laguna Phuket")}
              </li>
              <li>
                <span>4</span>
                {t("Cassia Hotel")}
              </li>
              <li>
                <span>5</span>
                {t("Canal Village Shopping")}
              </li>
              <li>
                <span>6</span>
                {t("Xana Beach Club")}
              </li>
              <li>
                <span>7</span>
                {t("Baan Talay Restaurant")}
              </li>
              <li>
                <span>8</span>
                {t("Angsana Poolside")}
              </li>
              <li>
                <span>9</span>
                {t("Bodega Restaurant")}
              </li>
              <li>
                <span>10</span>
                {t("Angsana Lobby")}
              </li>
              <li>
                <span>11</span>
                {t("Banyan Tree Spa")}
              </li>
              <li>
                <span>12</span>
                {t("Banyan Tree Lobby")}
              </li>
            </ul>
          </div>
        )}
        {lightboxIsOpen && (
          <Modal name="project-map-gallery-modal" onCancel={this.closeLightbox}>
            <GalleryLightbox
              currentImage={lightboxCurrentImage}
              images={_.map(lightboxImages, (value) => {
                return {
                  src: _.get(value, "url"),
                  caption: t(_.get(value, "name")),
                };
              })}
              onClickPrev={this.gotoPrevious}
              onClickNext={this.gotoNext}
              onClickThumbnail={this.gotoImage}
            />
          </Modal>
        )}
      </div>
    );
  }
}

export default ProjectMap;



// WEBPACK FOOTER //
// ./src/containers/ProjectMap/ProjectMap.js